import type { EventEnvelope } from '../types/envelope.js';
import type { OnErrorContext } from '../types/config.js';
import type { ListenerRegistry } from './registry.js';
import type { EnricherState } from '../enricher/enricher.js';
import { enrich, type TrackPayload } from '../enricher/enricher.js';

export interface DispatcherDeps {
  registry: ListenerRegistry;
  getEnricherState: () => EnricherState;
  onError?: (error: Error, context?: OnErrorContext) => void;
}

/**
 * Normalize payload → enrich → dispatch to all listeners (try/catch per listener).
 */
export function dispatchTrack(
  deps: DispatcherDeps,
  payload: TrackPayload
): void {
  const state = deps.getEnricherState();
  const envelope = enrich(payload, state);
  dispatchEnvelope(deps, envelope, 'track');
}

/**
 * Dispatch a pre-built envelope to all listeners (e.g. after enrich).
 */
function dispatchEnvelope(
  deps: DispatcherDeps,
  envelope: EventEnvelope,
  kind: 'track'
): void {
  const { registry, onError } = deps;
  for (const { id, listener } of registry.getAll()) {
    try {
      listener.track(envelope);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onError?.(error, { listenerId: id });
    }
  }
}

/**
 * Call identify on all listeners that implement it (try/catch per listener).
 */
export function dispatchIdentify(
  deps: DispatcherDeps,
  userId: string,
  traits?: Record<string, unknown>
): void {
  const { registry, onError } = deps;
  for (const { id, listener } of registry.getAll()) {
    if (typeof listener.identify !== 'function') continue;
    try {
      listener.identify(userId, traits);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onError?.(error, { listenerId: id });
    }
  }
}
